/**
 * Reusable Plinth Finish Fields Component
 * Used in both project wizard and element forms
 * Handles material, color (with "same as door" options), and height
 */

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Combobox } from '@/components/ui/combobox'

interface PlinthFinishFieldsProps {
  // Material
  material?: string
  materialSameAsDoor: boolean
  onMaterialChange: (material: string) => void
  onMaterialSameChange: (same: boolean) => void
  
  // Color
  color?: string
  colorSameAsDoor: boolean
  onColorChange: (color: string) => void
  onColorSameChange: (same: boolean) => void
  
  // Height
  height?: number
  onHeightChange: (height: number) => void
  
  // Options
  materialOptions?: string[]
}

export function PlinthFinishFields({
  material = '',
  materialSameAsDoor,
  onMaterialChange,
  onMaterialSameChange,
  color = '',
  colorSameAsDoor,
  onColorChange,
  onColorSameChange,
  height = 10,
  onHeightChange,
  materialOptions = ['Stratifié', 'Mélamine', 'MDF', 'Aluminium', 'PVC', 'Autre'],
}: PlinthFinishFieldsProps) {
  return (
    <div className="space-y-4">
      {/* Material */}
      <div className="space-y-2">
        <Label>Matériau</Label>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={materialSameAsDoor}
              onChange={() => onMaterialSameChange(true)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Identique aux portes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={!materialSameAsDoor}
              onChange={() => onMaterialSameChange(false)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Différent :</span>
            {!materialSameAsDoor && (
              <Combobox
                options={materialOptions.map((mat) => ({
                  value: mat,
                  label: mat,
                }))}
                value={material}
                onChange={onMaterialChange}
                placeholder="Matériau"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun matériau trouvé."
                allowCustomValue={true}
                className="flex-1"
              />
            )}
          </label>
        </div>
      </div>

      {/* Color */}
      <div className="space-y-2">
        <Label>Coloris</Label>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={colorSameAsDoor}
              onChange={() => onColorSameChange(true)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Identique aux portes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={!colorSameAsDoor}
              onChange={() => onColorSameChange(false)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Différent :</span>
            {!colorSameAsDoor && (
              <Input
                value={color}
                onChange={(e) => onColorChange(e.target.value)}
                placeholder="Coloris"
                className="flex-1"
              />
            )}
          </label>
        </div>
      </div>

      {/* Height */}
      <div className="space-y-2">
        <Label>Hauteur (cm)</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={height}
            onChange={(e) => onHeightChange(Number(e.target.value))}
            placeholder="10"
            className="w-24"
          />
          <span className="text-sm text-muted-foreground">cm</span>
        </div>
      </div>
    </div>
  )
}
